import type { RawRow } from './catalog.js'
import type { MerchantFieldKey, MerchantIssue, MerchantMapping, MerchantRow, MerchantRuleSet } from './merchant.js'

export const merchantRuleSet2026: MerchantRuleSet = {
  id: 'google-merchant-global',
  version: '2026.1',
  title: 'Google Merchant Center global product data rules',
  publishedAt: '2026-04-14',
  effectiveFrom: '2026-04-14',
  reviewedAt: '2026-08-09',
  sourceUrl: 'https://support.google.com/merchants/answer/7052112',
  notices: [
    'Google announced a 500 × 500 minimum image resolution with enforcement beginning January 31, 2027. This local file audit validates image URLs but cannot measure remote image dimensions.',
    'Country, category, apparel, shipping, tax, and policy-specific requirements need additional merchant context and are not certified by this MVP.',
  ],
}

const requiredFields: Array<{ field: MerchantFieldKey; label: string }> = [
  { field: 'id', label: 'ID' },
  { field: 'title', label: 'Title' },
  { field: 'description', label: 'Description' },
  { field: 'link', label: 'Link' },
  { field: 'imageLink', label: 'Image link' },
  { field: 'availability', label: 'Availability' },
  { field: 'price', label: 'Price' },
]

const getText = (row: RawRow, header: string): string => {
  if (!header) return ''
  const value = row[header]
  return value === null || value === undefined ? '' : String(value).trim()
}

const validUrl = (value: string): boolean => {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

const validPrice = (value: string): boolean => /^\d+(?:\.\d{1,2})?\s[A-Z]{3}$/.test(value)
const availabilityValues = new Set(['in_stock', 'out_of_stock', 'preorder', 'backorder'])
const conditionValues = new Set(['new', 'refurbished', 'used'])

export function isValidGtin(value: string): boolean {
  const digits = value.replace(/\s+/g, '')
  if (!/^\d+$/.test(digits) || ![8, 12, 13, 14].includes(digits.length)) return false
  const numbers = digits.split('').map(Number)
  const checkDigit = numbers.pop()!
  const sum = numbers.reverse().reduce((total, digit, index) => total + digit * (index % 2 === 0 ? 3 : 1), 0)
  return (10 - (sum % 10)) % 10 === checkDigit
}

export function validateMerchantRow(raw: RawRow, mapping: MerchantMapping, sourceRowNumber: number): MerchantRow {
    const issues: MerchantIssue[] = []
    const value = (field: MerchantFieldKey) => getText(raw, mapping[field])

    for (const required of requiredFields) {
      if (!mapping[required.field] || !value(required.field)) {
        issues.push({
          code: `missing_${required.field}`,
          severity: 'error',
          field: required.field,
          message: `${required.label} is required for every product.`,
          recommendation: `Map and populate the Google ${required.label.toLocaleLowerCase()} attribute.`,
        })
      }
    }

    const id = value('id')
    const title = value('title')
    const description = value('description')
    const link = value('link')
    const imageLink = value('imageLink')
    const availability = value('availability').toLocaleLowerCase()
    const price = value('price')
    const gtin = value('gtin')
    const condition = value('condition').toLocaleLowerCase()
    const identifierExists = value('identifierExists').toLocaleLowerCase()
    const brand = value('brand')
    const mpn = value('mpn')

    if (id.length > 50) issues.push({ code: 'id_too_long', severity: 'error', field: 'id', message: 'Product ID exceeds Google’s 50-character limit.', recommendation: 'Use a shorter stable product or variant ID and keep it unchanged in future feed updates.' })
    if (title.length > 150) issues.push({ code: 'title_too_long', severity: 'error', field: 'title', message: 'Title exceeds Google’s 150-character limit.', recommendation: 'Shorten the title while keeping the product identity and key variant details.' })
    if (description.length > 5_000) issues.push({ code: 'description_too_long', severity: 'error', field: 'description', message: 'Description exceeds Google’s 5,000-character limit.', recommendation: 'Remove repeated or low-value text and keep the description within 5,000 characters.' })
    if (link.length > 2_000) issues.push({ code: 'link_too_long', severity: 'error', field: 'link', message: 'Product link exceeds Google’s 2,000-character limit.', recommendation: 'Use a stable canonical product URL without unnecessary tracking parameters.' })
    if (imageLink.length > 2_000) issues.push({ code: 'image_link_too_long', severity: 'error', field: 'imageLink', message: 'Image link exceeds Google’s 2,000-character limit.', recommendation: 'Use a stable direct image URL without unnecessary parameters.' })
    if (link && !validUrl(link)) issues.push({ code: 'invalid_link', severity: 'error', field: 'link', message: 'Product link is not a valid HTTP(S) URL.', recommendation: 'Use the canonical public product page URL beginning with http:// or https://.' })
    if (imageLink && !validUrl(imageLink)) issues.push({ code: 'invalid_image_link', severity: 'error', field: 'imageLink', message: 'Image link is not a valid HTTP(S) URL.', recommendation: 'Use a directly accessible public image URL beginning with http:// or https://.' })
    if (availability && !availabilityValues.has(availability)) issues.push({ code: 'invalid_availability', severity: 'error', field: 'availability', message: `“${availability}” is not a supported availability value.`, recommendation: 'Use in_stock, out_of_stock, preorder, or backorder.' })
    if (price && !validPrice(price)) issues.push({ code: 'invalid_price', severity: 'error', field: 'price', message: 'Price must contain a numeric value and a three-letter ISO currency code.', recommendation: 'Use a format such as 29.99 USD or 24.50 EUR.' })
    if (condition && !conditionValues.has(condition)) issues.push({ code: 'invalid_condition', severity: 'error', field: 'condition', message: `“${condition}” is not a supported condition value.`, recommendation: 'Use new, refurbished, or used.' })
    if (identifierExists && !['yes', 'true', 'no', 'false'].includes(identifierExists)) issues.push({ code: 'invalid_identifier_exists', severity: 'error', field: 'identifierExists', message: `“${identifierExists}” is not a supported identifier_exists value.`, recommendation: 'Use yes, true, no, or false in English.' })
    if (gtin && !isValidGtin(gtin)) issues.push({ code: 'invalid_gtin', severity: 'error', field: 'gtin', message: 'GTIN has an invalid length, character, or check digit.', recommendation: 'Use the manufacturer-assigned 8, 12, 13, or 14-digit GTIN. Do not substitute an internal SKU.' })
    if (!['no', 'false', '0'].includes(identifierExists) && !gtin && !(brand && mpn)) {
      issues.push({ code: 'missing_product_identifier', severity: 'warning', field: 'gtin', message: 'No GTIN or brand + MPN combination was found.', recommendation: 'Provide manufacturer identifiers when they exist. If none exist, map identifier_exists and submit no.' })
    }

    return {
      sourceRowNumber,
      id,
      title,
      raw,
      status: issues.some((issue) => issue.severity === 'error') ? 'error' : issues.length ? 'warning' : 'pass',
      issues,
    }
}

export function validateMerchantFeed(rawRows: RawRow[], mapping: MerchantMapping): MerchantRow[] {
  const rows = rawRows.map((raw, index) => validateMerchantRow(raw, mapping, index + 2))

  const idCounts = new Map<string, number>()
  for (const row of rows) if (row.id) idCounts.set(row.id.toLocaleLowerCase(), (idCounts.get(row.id.toLocaleLowerCase()) ?? 0) + 1)

  return rows.map((row) => {
    if (!row.id || (idCounts.get(row.id.toLocaleLowerCase()) ?? 0) < 2) return row
    const issues = [...row.issues, { code: 'duplicate_id', severity: 'error', field: 'id', message: 'Product ID appears more than once.', recommendation: 'Give each product or variant a unique, stable ID.' } as MerchantIssue]
    return { ...row, issues, status: 'error' }
  })
}
